import React, { type ReactNode } from 'react';
import Spinner, { SPINNER_SIZE_SM } from '../../atoms/Spinner';
import {
  STEP_LIST_DENSITY_REGULAR,
  STEP_LIST_VARIANT_DOT,
  STEP_LIST_VARIANT_ICON,
  STEP_STATUS_CANCELLED,
  STEP_STATUS_FAILED,
  STEP_STATUS_IN_PROGRESS,
  STEP_STATUS_PENDING,
  STEP_STATUS_SKIPPED,
  STEP_STATUS_SUCCEEDED,
  type StepListDensity,
  type StepListVariant,
  type StepStatus,
} from './StepList.constants';
import {
  Body,
  Connector,
  Description,
  DotIndicator,
  DotItem,
  DotMarker,
  DotSpinnerIcon,
  Indicator,
  Item,
  Meta,
  Rail,
  Root,
  Title,
  TitleRow,
} from './StepList.styled';

export interface Step {
  /** Stable id used as the React key. */
  id: string;
  /** Visible title — usually a string, but accepts richer nodes. */
  title: ReactNode;
  /** Status — drives the indicator glyph + colour. */
  status: StepStatus;
  /** Optional description rendered under the title. */
  description?: ReactNode;
  /**
   * Optional meta line — typically a duration ("1.2s"), timestamp
   * ("12:04:33"), or count ("3/8"). Pinned to the right edge of the
   * title row, with tabular numerals so values line up.
   */
  meta?: ReactNode;
  /**
   * Optional rich body rendered below the description. Use for
   * collapsible logs, sub-steps, error messages, etc. Typically you'd
   * combine this with `<ExpandableRow>` if you want it collapsible.
   */
  body?: ReactNode;
  /**
   * Override the indicator icon for this step (e.g. a custom glyph for
   * "waiting on user"). The default per-status icon is replaced.
   */
  icon?: ReactNode;
}

export interface StepListProps {
  steps: Step[];
  /** Vertical density. Defaults to `regular`. */
  density?: StepListDensity;
  /**
   * Visual variant. `icon` (default) renders filled circles with SVG
   * icons and vertical connector lines. `dot` renders plain small dots
   * with no connectors — a lighter timeline style suited to execution
   * logs and sidebar panels.
   */
  variant?: StepListVariant;
  /**
   * Override icon registry for an entire status. Useful when the
   * consumer wants, e.g., a different "succeeded" glyph throughout.
   * Per-step `icon` still wins. Only applies to `variant="icon"`.
   */
  iconOverrides?: Partial<Record<StepStatus, ReactNode>>;
  ariaLabel?: string;
  className?: string;
  dataTestId?: string;
}

const Check = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M5 12.5 10 17 19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Cross = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6 6 18 18 M18 6 6 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const Dash = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6 12h12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const Slash = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6 18 18 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

const DEFAULT_ICONS: Record<StepStatus, ReactNode> = {
  [STEP_STATUS_PENDING]: null,
  // Inline spinner — the indicator wrapper already announces the
  // status to screen readers via its own role="img" + aria-label, so
  // pass an empty `label` here to suppress duplicate "Loading" reads.
  [STEP_STATUS_IN_PROGRESS]: <Spinner size={SPINNER_SIZE_SM} label="" />,
  [STEP_STATUS_SUCCEEDED]: <Check />,
  [STEP_STATUS_FAILED]: <Cross />,
  [STEP_STATUS_SKIPPED]: <Dash />,
  [STEP_STATUS_CANCELLED]: <Slash />,
};

const STATUS_LABEL: Record<StepStatus, string> = {
  [STEP_STATUS_PENDING]: 'Pending',
  [STEP_STATUS_IN_PROGRESS]: 'In progress',
  [STEP_STATUS_SUCCEEDED]: 'Succeeded',
  [STEP_STATUS_FAILED]: 'Failed',
  [STEP_STATUS_SKIPPED]: 'Skipped',
  [STEP_STATUS_CANCELLED]: 'Cancelled',
};

/**
 * Vertical list of steps with status-coded indicators and connector
 * lines — the universal "build / run / wizard / progress" pattern.
 *
 * The indicator is auto-derived from `status` (filled disc + glyph for
 * completed states, hollow ring for pending, animated `Spinner` for
 * in-progress). The connector line under each step takes the *upper*
 * step's tone so a finished step paints a green line below itself.
 *
 * Each step's `body` slot is the escape hatch for richer content —
 * pass an `<ExpandableRow>` there if you want collapsible logs.
 *
 * @example
 *   <StepList
 *     ariaLabel="Print job progress"
 *     steps={[
 *       { id: 's1', title: 'Slice', status: 'succeeded', meta: '1.2s' },
 *       { id: 's2', title: 'Validate', status: 'succeeded', meta: '0.4s' },
 *       { id: 's3', title: 'Print', status: 'inProgress', meta: '34s' },
 *       { id: 's4', title: 'Verify', status: 'pending' },
 *     ]}
 *   />
 */
const StepList: React.FC<StepListProps> = ({
  steps,
  density = STEP_LIST_DENSITY_REGULAR,
  variant = STEP_LIST_VARIANT_ICON,
  iconOverrides,
  ariaLabel,
  className,
  dataTestId = 'step-list',
}) => {
  const isDot = variant === STEP_LIST_VARIANT_DOT;

  const iconFor = (step: Step) => {
    if (step.icon !== undefined) return step.icon;
    if (iconOverrides?.[step.status] !== undefined)
      return iconOverrides[step.status];
    return DEFAULT_ICONS[step.status];
  };

  return (
    <Root
      aria-label={ariaLabel}
      className={className}
      data-testid={dataTestId}
      data-density={density}
      data-variant={variant}
    >
      {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;

        if (isDot) {
          return (
            <DotItem
              key={step.id}
              data-testid={`${dataTestId}__item`}
              data-status={step.status}
            >
              <DotMarker
                role="img"
                aria-label={STATUS_LABEL[step.status]}
                data-testid={`${dataTestId}__indicator`}
              >
                {step.status === STEP_STATUS_IN_PROGRESS ? (
                  <DotSpinnerIcon className="material-symbols-rounded">
                    progress_activity
                  </DotSpinnerIcon>
                ) : (
                  <DotIndicator $status={step.status} />
                )}
              </DotMarker>
              <Body
                $density={density}
                $isLast={isLast}
                $dot
                data-testid={`${dataTestId}__body`}
              >
                <TitleRow>
                  <Title
                    $density={density}
                    $skipped={step.status === STEP_STATUS_SKIPPED}
                    data-testid={`${dataTestId}__title`}
                  >
                    {step.title}
                  </Title>
                  {step.meta !== undefined && step.meta !== null && (
                    <Meta
                      $density={density}
                      data-testid={`${dataTestId}__meta`}
                    >
                      {step.meta}
                    </Meta>
                  )}
                </TitleRow>
                {step.description !== undefined &&
                  step.description !== null && (
                    <Description
                      $density={density}
                      data-testid={`${dataTestId}__description`}
                    >
                      {step.description}
                    </Description>
                  )}
                {step.body !== undefined && step.body !== null && (
                  <div data-testid={`${dataTestId}__step-body`}>
                    {step.body}
                  </div>
                )}
              </Body>
            </DotItem>
          );
        }

        const indicatorIcon = iconFor(step);

        return (
          <Item
            key={step.id}
            $density={density}
            data-testid={`${dataTestId}__item`}
            data-status={step.status}
          >
            <Rail $density={density}>
              <Indicator
                $status={step.status}
                $density={density}
                role="img"
                aria-label={STATUS_LABEL[step.status]}
                data-testid={`${dataTestId}__indicator`}
              >
                {indicatorIcon}
              </Indicator>
              {!isLast && (
                <Connector
                  $status={step.status}
                  $density={density}
                  data-testid={`${dataTestId}__connector`}
                />
              )}
            </Rail>
            <Body
              $density={density}
              $isLast={isLast}
              data-testid={`${dataTestId}__body`}
            >
              <TitleRow>
                <Title
                  $density={density}
                  $skipped={step.status === STEP_STATUS_SKIPPED}
                  data-testid={`${dataTestId}__title`}
                >
                  {step.title}
                </Title>
                {step.meta !== undefined && step.meta !== null && (
                  <Meta
                    $density={density}
                    data-testid={`${dataTestId}__meta`}
                  >
                    {step.meta}
                  </Meta>
                )}
              </TitleRow>
              {step.description !== undefined &&
                step.description !== null && (
                  <Description
                    $density={density}
                    data-testid={`${dataTestId}__description`}
                  >
                    {step.description}
                  </Description>
                )}
              {step.body !== undefined && step.body !== null && (
                <div data-testid={`${dataTestId}__step-body`}>
                  {step.body}
                </div>
              )}
            </Body>
          </Item>
        );
      })}
    </Root>
  );
};

export default StepList;
